import React from 'react';
import { visibleCodFields, type CodFieldValue, type CodFormConfig, type CodRenderField } from '@xshop/shared';

interface CodFormFieldsProps {
  config: CodFormConfig;
  values: Record<string, CodFieldValue>;
  errors: Record<string, string>;
  onChange: (key: string, value: CodFieldValue) => void;
}

interface ControlProps {
  field: CodRenderField;
  value: CodFieldValue | undefined;
  invalid: boolean;
  onChange: (value: CodFieldValue) => void;
}

// Names the id a field's message is drawn under, which its control points at while the message shows.
export const codErrorId = (key: string): string => `cod-${key}-error`;

// Names the id of a field's own control, which its label points at and a refused submission focuses.
export const codControlId = (key: string): string => `cod-${key}`;

// The autocomplete token a browser needs to offer a saved address for this field.
const AUTOCOMPLETE: Record<string, string> = Object.assign(Object.create(null), {
  fullName: 'name', firstName: 'given-name', lastName: 'family-name', phone: 'tel', email: 'email',
  address1: 'address-line1', address2: 'address-line2', city: 'address-level2',
  region: 'address-level1', postalCode: 'postal-code', country: 'country-name'
});

// The input this field's type calls for, wired to the one keyed value map the form submits.
const CodControl: React.FC<ControlProps> = ({ field, value, invalid, onChange }) => {
  const className = `form-input ${invalid ? 'input-error' : ''}`;
  const shared = {
    id: codControlId(field.key),
    name: field.key,
    placeholder: field.placeholder || undefined,
    autoComplete: AUTOCOMPLETE[field.key],
    dir: 'auto' as const,
    'aria-invalid': invalid || undefined,
    'aria-required': field.required || undefined,
    'aria-describedby': invalid ? codErrorId(field.key) : undefined
  };

  if (field.type === 'TEXTAREA') {
    return <textarea {...shared} className={className} rows={2} value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'SELECT') {
    return (
      <select {...shared} className={className} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
        <option value="">{field.placeholder || '—'}</option>
        {field.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }
  if (field.type === 'CHECKBOX') {
    return <input {...shared} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
  }
  return <input {...shared} type={field.type === 'NUMBER' ? 'number' : inputType(field.key)} className={className}
    value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />;
};

// The HTML input type a built-in field deserves, so phones get a numeric keypad on mobile.
function inputType(key: string): string {
  if (key === 'phone') return 'tel';
  if (key === 'email') return 'email';
  return 'text';
}

// Renders the merchant's configured checkout form: their fields, their labels, their placeholders.
export const CodFormFields: React.FC<CodFormFieldsProps> = ({ config, values, errors, onChange }) => (
  <>
    {visibleCodFields(config).map((field) => (
      <div className="form-group" key={field.key}>
        {field.type === 'CHECKBOX' ? (
          <label className="form-label" htmlFor={codControlId(field.key)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CodControl field={field} value={values[field.key]} invalid={Boolean(errors[field.key])}
              onChange={(next) => onChange(field.key, next)} />
            <span dir="auto">{field.label}{field.required ? ' *' : ''}</span>
          </label>
        ) : (
          <>
            <label className="form-label" htmlFor={codControlId(field.key)} dir="auto">
              {field.label}{field.required ? ' *' : ''}
            </label>
            <CodControl field={field} value={values[field.key]} invalid={Boolean(errors[field.key])}
              onChange={(next) => onChange(field.key, next)} />
          </>
        )}
        {errors[field.key] && <span className="field-error" id={codErrorId(field.key)} dir="auto">{errors[field.key]}</span>}
      </div>
    ))}
  </>
);
